import { Dropdown } from '../components/Dropdown';
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  api,
  ApiError,
  type CommentResolution,
  type CommentVisibility,
  type ContractCardView,
  type ContractCommentInput,
  type ContractCommentView,
  type ContractParagraph,
} from '../api';
import { useApp } from '../App';
import { IconDownload, IconExpand, IconMinimize, IconMinus, IconPlus } from '../components/icons';
import { useIsMobile } from '../lib/media';
import { PanelHead } from '../nav/PanelHead';
import { HostLayer, StackHost } from '../nav/StackHost';
import { useBackEntry } from '../nav/useBackEntry';
import { DocPages, type DocAnchor, type DocHighlight } from './DocPages';
import { Note, NoteBody, type NoteActions } from './Note';
import {
  clauseHint,
  draftParagraph,
  draftState,
  placeNotes,
  type SnapshotState,
} from './notes';
import { commentingNote, docEntries, docFeed, previewGapNote, type DocItem } from './registry';

/*
 * Зона «Документ»: панель инструментов, страницы версии и ветки замечаний
 * пузырями на полях (3.55, 3.56 — выбор Ильи 17.09.2026, вариант Б).
 *
 * Зона одна на два стола. У держателя это часть карточки договора, у
 * подрядчика — вся гостевая страница, и различий между ними ровно столько,
 * сколько перечислено в `DocSource`: откуда берутся версии и разговор, что
 * можно делать и что показывать. Поэтому не флаг «гость» посреди разметки, а
 * один источник: гостю просто нечего передать в поля, которых у него нет —
 * ни сравнения версий, ни ответа по существу, ни docx с примечаниями.
 *
 * Документ — одно полотно, и поле пузырей тоже одно (3.55, 3.57, задача Ильи
 * 21.09.2026). Места якорей считает `DocPages` по слепку текста всего полотна
 * и отдаёт их полю в пикселях от верха полотна вместе с номером страницы —
 * номер нужен только адресу «С. 3». Высоты пузырей меряются после первого
 * вывода, и `placeNotes` разводит наезжающие. Ни то ни другое не хранится на
 * сервере: полотно перерисовывается при любой ширине окна и любом масштабе.
 *
 * Номер абзаца новому замечанию нужен, чтобы сервер подписал ветку номером
 * пункта и перенёс якорь на следующую версию. Слепок версии отдельным
 * маршрутом не отдаётся, поэтому он берётся сравнением версии с самой собой:
 * все строки выходят «без изменений», и в них лежат абзацы со своими
 * номерами.
 *
 * На ширине 900 и уже полей нет вовсе: на выделениях счётчики, а ветка и
 * форма уходят слоем стопки — на телефоне это отдельный экран со стрелкой
 * «назад», а не всплывающее окно без выхода.
 */

/** Откуда зона берёт документ и разговор и что в ней разрешено. */
export type DocSource = {
  /** Договор или гостевая ссылка: по нему зона решает, что перечитывать. */
  key: string;
  /**
   * Все версии по порядку; показываются те, у которых есть PDF. Признак
   * скана нужен подписи: скан зовётся сканом, а не очередной версией круга
   * (registry.ts). У гостя его нет — скана в его списке не бывает.
   */
  versions: { id: string; placeId?: string; round: number; createdAt: string; hasPdf: boolean; signed?: boolean }[];
  /**
   * Места версии по порядку: сам договор, приложение, смета. Лента идёт по
   * ним сверху вниз, и замечания каждого места живут у своего документа.
   */
  places: { id: string; title: string }[];
  statusLabel: string;
  round: number;
  /** Можно ли писать замечания: договор в комментировании и есть право. */
  canComment: boolean;
  /**
   * Почему писать нельзя, если нельзя. Зона живёт от заведения договора до
   * подписи и между кругами читается, но не правится; молчаливая зона без
   * кнопок читается как поломка, поэтому причина стоит словами над листом.
   * Пусто — круг идёт.
   */
  closedNote: string;
  /** Держатель: фильтр внутренних веток, ответ по существу, docx с примечаниями. */
  holder: boolean;
  /** Гость: внутренних веток не видит вовсе, поэтому ни фильтров, ни метки круга. */
  guest: boolean;
  pdfUrl: (versionId: string) => string;
  /** Адрес docx с примечаниями по месту; пусто — кнопки нет. */
  exportUrl: (placeId: string) => string;
  /** Сравнение версий; `null` — пункта нет (у гостя такого маршрута не существует). */
  onCompare: (() => void) | null;
  list: () => Promise<ContractCommentView[]>;
  add: (body: ContractCommentInput) => Promise<unknown>;
  /**
   * Разбор версии: по нему у нового замечания появляется номер пункта. У
   * гостя его нет, и место ветки сервер находит по самой цитате.
   */
  snapshot: ((versionId: string) => Promise<SnapshotState>) | null;
  /** Своя ли ветка: видимость меняет её автор. */
  mine: (thread: ContractCommentView) => boolean;
  setVisibility?: (id: string, visibility: CommentVisibility) => Promise<unknown>;
  resolve?: (id: string, resolution: CommentResolution, note: string) => Promise<unknown>;
};

/**
 * Масштаб полотна: доля от ширины зоны. Единица — «по ширине», исходное
 * положение; ниже неё документ уменьшается, выше — вылезает за область
 * прокрутки вместе с полем, и прокручивается вбок сама зона.
 */
const ZOOMS = [0.75, 1, 1.25, 1.5, 1.75, 2];

/*
 * Выбранный масштаб переживает и перезагрузку, и другой договор: человек не
 * должен подбирать кегль каждый раз заново. Хранится в браузере, а не на
 * сервере, и намеренно: комментирует чаще всего подрядчик по гостевой ссылке,
 * у которого учётной записи нет вовсе, а нужный масштаб задаёт экран, за
 * которым человек сидит, — это свойство устройства, как свёрнутое меню.
 */
const ZOOM_KEY = 'code-doc-zoom';

const savedZoom = (): number => {
  const v = Number(localStorage.getItem(ZOOM_KEY));
  return ZOOMS.includes(v) ? v : 1;
};

/**
 * `top` — пиксели от верха полотна того документа, в котором идёт выделение:
 * поле пузырей своё у каждого места ленты, и система координат у него своя.
 * Место и версия запоминаются вместе с выделением — иначе замечание ушло бы к
 * документу, который человек в этот момент даже не читал.
 */
type Draft = { top: number; quote: string; versionId: string; placeId: string };

/** Ветка или форма нового замечания на поле одной страницы. */
type MarginItem = {
  key: string;
  /** Пиксели от верха полотна: поле и полотно — одна сетка, высота у них общая. */
  anchorTop: number;
  render: (place: { top: number; pushed: boolean; push: number }) => ReactNode;
};

/** Зона документа в карточке договора: источник — сам договор и права сотрудника. */
export function DocView({ contract, onCompare }: { contract: ContractCardView; onCompare: () => void }) {
  const app = useApp();
  const holder = contract.roles.includes('holder') || contract.roles.includes('admin');
  const canComment = contract.status === 'COMMENTING' && contract.canComment;
  const login = app.user.login;

  const doc: DocSource = useMemo(
    () => ({
      key: contract.id,
      versions: contract.versions,
      places: contract.places,
      statusLabel: contract.statusLabel,
      round: contract.round,
      canComment,
      closedNote: commentingNote(contract.status, contract.round, canComment),
      holder,
      guest: false,
      pdfUrl: (versionId) => api.contractPdfUrl(contract.id, versionId),
      exportUrl: (placeId) => (holder ? api.contractExportUrl(contract.id, placeId) : ''),
      onCompare,
      list: () => api.contractComments(contract.id),
      add: (body) => api.addContractComment(contract.id, body),
      snapshot: (versionId) =>
        api.contractCompare(contract.id, versionId, versionId).then((res) => {
          const paragraphs = res.rows
            .map((r) => r.b ?? r.a)
            .filter((p): p is ContractParagraph => !!p);
          return res.ready ? { state: 'ready', paragraphs } : { state: 'failed' };
        }),
      mine: (t) => 'login' in t.author && t.author.login === login,
      setVisibility: (id, visibility) => api.patchContractComment(contract.id, id, { visibility }),
      resolve: (id, resolution, note) => api.patchContractComment(contract.id, id, { resolution, note }),
    }),
    [contract, canComment, holder, login, onCompare],
  );

  return <DocZone doc={doc} />;
}

export function DocZone({ doc }: { doc: DocSource }) {
  const mobile = useIsMobile();
  const versions = doc.versions;
  /*
   * Переключается лента кругами, а не файлами: версия это набор мест, и в
   * круге их несколько. Показываются круги, у которых есть чем показать хоть
   * одно место.
   */
  const entries = useMemo(() => docEntries(versions), [versions]);
  const shown = useMemo(() => entries.filter((e) => e.hasPdf), [entries]);
  const last = shown.length ? shown[shown.length - 1].key : '';

  const [entryKey, setEntryKey] = useState(last);
  const [threads, setThreads] = useState<ContractCommentView[]>([]);
  /** Разборы документов ленты по файлам: у каждого места свой слепок. */
  const [snaps, setSnaps] = useState<Record<string, SnapshotState>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [draftErr, setDraftErr] = useState('');
  /*
   * Полный экран (3.57). Состояние — здесь, а не в адресе и не на сервере:
   * это рабочее положение на время чтения, как открытое окно, а не настройка
   * показа. Переключается класс на том же узле, компонент не пересоздаётся,
   * поэтому версия, фильтр, выбранная ветка и набранный черновик режим
   * переживают.
   */
  const [full, setFull] = useState(false);
  /*
   * Масштаб полотна (3.57). В отличие от полного экрана он переживает не
   * только переключение режима, но и весь заход: это не рабочее положение на
   * время чтения, а то, каким кеглем человеку читать.
   */
  const [zoom, setZoom] = useState(savedZoom);
  /** Область прокрутки ленты: одна на все документы версии. */
  const scroll = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    localStorage.setItem(ZOOM_KEY, String(zoom));
  }, [zoom]);
  const stepZoom = (d: number) =>
    setZoom((v) => ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, ZOOMS.indexOf(v) + d))]);

  // Новая версия — новый выбор: договор перечитывается после отправки круга.
  useEffect(() => {
    setEntryKey((cur) => (shown.some((e) => e.key === cur) ? cur : last));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [last, shown.length]);

  const entry = useMemo(() => entries.find((e) => e.key === entryKey) ?? null, [entries, entryKey]);
  /* Документы круга по одному на место, в порядке мест: это и есть лента. */
  const feed = useMemo(() => docFeed(doc.places, versions, entry), [doc.places, versions, entry]);

  const reload = () =>
    doc
      .list()
      .then(setThreads)
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Не удалось прочитать замечания'));

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.key]);

  // Слепки документов ленты: номера пунктов для нового замечания. Пока слепок
  // не прочитан, отправлять нечего — иначе замечание ушло бы к нулевому
  // абзацу, то есть к заголовку договора. У гостя слепка нет вовсе, и это не
  // ошибка: место его ветки сервер находит по цитате.
  const readSnapshot = doc.snapshot;
  /*
   * Вкладка на каждый файл круга (замечание Ильи 25.09.2026): договор и его
   * допсоглашения в пакете — отдельные документы, и одной лентой друг под
   * другом они терялись. Разговор по всем вкладкам идёт параллельно:
   * замечания каждого файла живут на своём месте, а переключение только
   * выбирает, какой файл на экране. Из одного файла вкладок нет.
   */
  const [placeTab, setPlaceTab] = useState('');
  const tabbed = feed.length > 1;
  const activePlace = tabbed ? (feed.find((f) => f.placeId === placeTab) ?? feed[0]).placeId : '';
  const onScreen = tabbed ? feed.filter((f) => f.placeId === activePlace) : feed;
  const feedIds = feed.map((f) => f.versionId).join('|');
  useEffect(() => {
    if (!readSnapshot) return;
    let alive = true;
    const ids = feedIds ? feedIds.split('|') : [];
    setSnaps(Object.fromEntries(ids.map((id) => [id, { state: 'loading' } as SnapshotState])));
    for (const id of ids) {
      readSnapshot(id)
        .then((res) => {
          if (alive) setSnaps((was) => ({ ...was, [id]: res }));
        })
        .catch(() => {
          if (alive) setSnaps((was) => ({ ...was, [id]: { state: 'failed' } }));
        });
    }
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.key, feedIds]);

  // Фильтров по видам замечаний больше нет (решение Ильи 25.09.2026): на
  // экране все ветки документа.
  const visible = threads;
  const byId = useMemo(() => new Map(visible.map((t) => [t.id, t])), [visible]);

  /*
   * Ветки разводятся по местам, и это главное правило ленты: замечание к
   * смете живёт у сметы и никогда не всплывает у договора, даже если цитата
   * случайно встречается и там. Место ветки приходит с сервера и выводится
   * из версии, на которой стоит её якорь.
   *
   * Договоры, заведённые до того, как версия стала набором мест, места у
   * веток не имеют вовсе: такие ветки показываются у первого документа
   * ленты — единственного, который у них и был.
   */
  const placeOf = (t: ContractCommentView): string =>
    t.placeId || (doc.places.length ? doc.places[0].id : '');
  const atPlace = (list: ContractCommentView[], placeId: string) =>
    list.filter((t) => placeOf(t) === placeId);

  // Выбрали ветку другого файла — из списка или по адресу замечания: вкладка
  // переключается на её файл, иначе выбранное оставалось бы невидимым.
  useEffect(() => {
    if (!tabbed || !selected) return;
    const t = threads.find((x) => x.id === selected);
    if (t && placeOf(t) !== activePlace) setPlaceTab(placeOf(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const run = async (call: () => Promise<unknown>) => {
    setBusy(true);
    setErr('');
    try {
      await call();
      await reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  /*
   * Разбор версии есть не всегда: у гостя его нет вовсе, а у docx, который
   * сервер не сумел разобрать, он остаётся `failed` навсегда. Оба случая для
   * формы одинаковы — пункт неизвестен, замечание уходит с цитатой.
   */
  const snapAt = (id: string): SnapshotState | null => (readSnapshot ? (snaps[id] ?? { state: 'loading' }) : null);
  const drafting = draftState(draft ? snapAt(draft.versionId) : null);
  const gap = previewGapNote(entries, entryKey);

  const address = (t: ContractCommentView, page?: number): string => {
    const parts = [];
    if (page !== undefined) parts.push(`С. ${page + 1}`);
    if (t.label) parts.push(`П. ${t.label.replace(/\.$/, '')}`);
    return parts.join(' · ');
  };

  const actionsFor = (t: ContractCommentView): NoteActions => {
    const setVisibility = doc.setVisibility;
    const resolve = doc.resolve;
    return {
      canComment: doc.canComment,
      showVisibility: !doc.guest,
      canVisibility: !!setVisibility && (doc.holder || doc.mine(t)),
      canResolve: !!resolve && doc.holder,
      busy,
      onReply: (text) =>
        void run(() =>
          doc.add({
            versionId: t.versionId,
            paragraph: t.paragraph,
            quote: t.quote,
            text,
            parentId: t.id,
          }),
        ),
      onVisibility: (visibility) => {
        if (setVisibility) void run(() => setVisibility(t.id, visibility));
      },
      onResolve: (resolution, note) => {
        if (resolve) void run(() => resolve(t.id, resolution, note));
      },
    };
  };

  const sheet = mobile ? (selected && byId.get(selected)) || null : null;
  /*
   * Закрытие уносит и ошибку отправки: плашка живёт внутри листа, и
   * несохранённая правка встречала следующее открытие чужой ошибкой — ветка
   * уже другая, а «Не удалось сохранить» то же самое.
   */
  const closeSheet = () => {
    setSelected(null);
    setDraft(null);
    setDraftErr('');
    setErr('');
  };
  useBackEntry(mobile && (!!sheet || !!draft), closeSheet);

  // На телефоне документ и так во весь экран: режима там нет, и оставшийся с
  // широкого окна класс снимается при сужении.
  useEffect(() => {
    if (mobile) setFull(false);
  }, [mobile]);

  // Esc сворачивает документ и больше ничего: открытый черновик замечания он
  // не трогает — тот переживает выход и остаётся на месте.
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);

  // Системная «назад» сворачивает документ, а не уводит из договора: запись в
  // истории та же, что у слоя стопки.
  useBackEntry(full, () => setFull(false));

  if (!feed.length) {
    /*
     * Пустая зона бывает от двух разных причин, и подрядчику по ссылке их
     * смешивать нельзя: у него, кроме этой зоны, на странице ничего нет, и
     * одно молчаливое «показывать нечего» он читает как сломанную ссылку.
     * Поэтому «текста ещё не выложили» говорится словами и с прямой
     * оговоркой, что ссылка рабочая, а «не собрался предпросмотр» остаётся
     * отдельным случаем — он про PDF, а не про договор.
     */
    const blank = !versions.length
      ? doc.guest
        ? { head: 'Текст договора ещё не выложен', note: 'Ссылка рабочая: версия появится здесь, как только её загрузят' }
        : { head: 'Версий пока нет', note: 'Файл договора загружает инициатор в черновике' }
      : { head: 'Показывать нечего', note: 'Предпросмотр появится, когда версия договора превратится в PDF' };
    return (
      <div className="card">
        <div className="cnt-head">
          <h2>Документ</h2>
        </div>
        <div className="empty">
          <b>{blank.head}</b>
          {blank.note}
        </div>
      </div>
    );
  }

  const form = (place?: { top: number; pushed: boolean; push: number }) =>
    draft && (
      <DraftForm
        key="draft"
        quote={draft.quote}
        place={place}
        busy={busy}
        ready={drafting.ready}
        hint={drafting.hint}
        shareable={!doc.guest}
        error={draftErr}
        onCancel={() => {
          setDraft(null);
          setDraftErr('');
        }}
        onSend={(text, proposal, shared) => {
          // Пункт не угадывается: разбор прочитан, а цитаты в нём нет —
          // замечание не уходит вовсе. Разбора нет или он не удался — уходит
          // без номера, место сервер найдёт по цитате.
          const at = draftParagraph(snapAt(draft.versionId), draft.quote);
          if ('error' in at) {
            setDraftErr(at.error);
            return;
          }
          setDraftErr('');
          void run(async () => {
            await doc.add({
              versionId: draft.versionId,
              paragraph: at.i,
              quote: draft.quote,
              text,
              proposal,
              ...(doc.guest ? {} : { visibility: shared ? 'SHARED' : 'INTERNAL' }),
            });
            setDraft(null);
          });
        }}
      />
    );

  /*
   * Телефон: ветка и форма — слой стопки, а не всплывающее окно. Слой даёт
   * шапку со стрелкой «назад» и полный экран (`.nav-layer .dialog` в ui.css);
   * системная «назад» закрывает его через `useBackEntry`.
   */
  const sheetNode =
    mobile && (!!sheet || !!draft) ? (
      <div className="backdrop" onClick={(e) => e.target === e.currentTarget && closeSheet()}>
        <div className="dialog" role="dialog" aria-modal="true">
          <PanelHead title={sheet ? 'Замечание' : 'Новое замечание'} onBack={closeSheet} />
          <div className="dialog-body">
            {/* Плашка ошибки живёт в корне, а корень под слоем стопки inert:
                на телефоне неудачная отправка была не видна вовсе. */}
            {!!err && (
              <div className="banner crit">
                <b>{err}</b>
              </div>
            )}
            {sheet ? (
              <NoteBody
                thread={sheet}
                address={address(sheet)}
                actions={actionsFor(sheet)}
              />
            ) : (
              form()
            )}
          </div>
        </div>
      </div>
    ) : null;
  const layers: HostLayer[] = sheetNode ? [{ key: 'doc-note', kind: 'modal', node: sheetNode }] : [];

  const root = (
    <div className={full ? 'ct-doc full' : 'ct-doc'}>
      <div className="doc-bar">
        {/* Какой документ на экране — выпадающим списком первым в панели
            (решение Ильи 25.09.2026): договор, приложения и допсоглашения
            пакета комментируются каждый своим, разговор по ним параллельный. */}
        {tabbed && (
          <Dropdown
            value={activePlace}
            ariaLabel="Документ"
            onChange={(id) => {
              setPlaceTab(id);
              setSelected(null);
              setDraft(null);
            }}
            options={feed.map((f) => ({
              value: f.placeId,
              label: `${f.title} · ${atPlace(visible, f.placeId).length}`,
            }))}
          />
        )}
        <div className="seg">
          {shown.map((e) => (
            <button
              key={e.key}
              className={e.key === entryKey ? 'on' : ''}
              type="button"
              onClick={() => {
                setEntryKey(e.key);
                closeSheet();
              }}
            >
              {e.label}
            </button>
          ))}
        </div>
        {/* Замечание уходит к версии на экране: если последней версии в
            списке нет предпросмотра, об этом говорится прямо. */}
        {!!gap && <span className="status warn">{gap}</span>}
        {!!doc.onCompare && versions.length > 1 && (
          <button className="btn link sm" type="button" onClick={doc.onCompare}>
            Сравнить
          </button>
        )}
        <div className="grow" />
        <span className="status active">
          {doc.statusLabel} · круг {doc.round}
        </span>
        {/* Масштаб — существующие контролы: две `.ibtn.sm` и ссылка с числом
            процентов, возвращающая полотно к ширине зоны (3.57, приём 3.45). */}
        {!mobile && (
          <>
            <button
              className="ibtn sm"
              type="button"
              aria-label="Мельче"
              title="Мельче"
              disabled={zoom <= ZOOMS[0]}
              onClick={() => stepZoom(-1)}
            >
              <IconMinus />
            </button>
            <button
              className="btn link sm"
              type="button"
              title="Вернуть по ширине зоны"
              onClick={() => setZoom(1)}
            >
              {Math.round(zoom * 100)} %
            </button>
            <button
              className="ibtn sm"
              type="button"
              aria-label="Крупнее"
              title="Крупнее"
              disabled={zoom >= ZOOMS[ZOOMS.length - 1]}
              onClick={() => stepZoom(1)}
            >
              <IconPlus />
            </button>
          </>
        )}
        {!mobile && (
          <button
            className="ibtn sm"
            type="button"
            aria-pressed={full}
            aria-label={full ? 'Свернуть документ' : 'Развернуть документ на весь экран'}
            title={full ? 'Свернуть (Esc)' : 'Развернуть на весь экран'}
            onClick={() => setFull((v) => !v)}
          >
            {full ? <IconMinimize /> : <IconExpand />}
          </button>
        )}
      </div>

      {/* Круг закрыт — это состояние страницы, а не ошибка: плашка обычная,
          и говорит она, почему кнопок не видно. */}
      {!!doc.closedNote && (
        <div className="banner">
          <b>{doc.closedNote}</b>
        </div>
      )}

      {!!err && (
        <div className="banner crit">
          <b>{err}</b>
        </div>
      )}

      {/* Прокручиваемая область одна: экран прокручивается ровно один
          (правило телефона). */}
      <div className="doc-scroll" ref={scroll}>
        {onScreen.map((item) => (
          <PlaceDoc
            key={item.versionId}
            item={item}
            single={!tabbed}
            exportUrl={item.placeId ? doc.exportUrl(item.placeId) : ''}
            pdfUrl={doc.pdfUrl(item.versionId)}
            scroll={scroll}
            threads={item.placeId ? atPlace(visible, item.placeId) : []}
            selected={selected}
            snap={snapAt(item.versionId)}
            zoom={mobile ? 1 : zoom}
            mobile={mobile}
            canComment={doc.canComment}
            draft={draft && draft.versionId === item.versionId ? draft : null}
            onSelectHighlight={(id) => {
              setSelected(id);
              setDraft(null);
              setDraftErr('');
            }}
            onSelectText={(sel) => {
              setSelected(null);
              setDraft({ ...sel, versionId: item.versionId, placeId: item.placeId });
              setDraftErr('');
            }}
            onSelect={setSelected}
            address={address}
            actionsFor={actionsFor}
            form={form}
          />
        ))}
      </div>
    </div>
  );

  return <StackHost root={root} layers={layers} />;
}

/**
 * Один документ ленты: название места, его страницы и поле пузырей рядом.
 *
 * Своим компонентом, а не куском разметки в цикле, потому что у каждого
 * документа своя система координат: места якорей `DocPages` считает по
 * своему полотну, и складывать пузыри двух документов в одно поле было бы
 * нечем — у них разные нули.
 */
function PlaceDoc({
  item,
  single,
  exportUrl,
  pdfUrl,
  scroll,
  threads,
  selected,
  snap,
  zoom,
  mobile,
  canComment,
  draft,
  onSelectHighlight,
  onSelectText,
  onSelect,
  address,
  actionsFor,
  form,
}: {
  item: DocItem;
  single: boolean;
  exportUrl: string;
  pdfUrl: string;
  scroll: RefObject<HTMLDivElement | null>;
  threads: ContractCommentView[];
  selected: string | null;
  snap: SnapshotState | null;
  zoom: number;
  mobile: boolean;
  canComment: boolean;
  draft: Draft | null;
  onSelectHighlight: (id: string) => void;
  onSelectText: (sel: { quote: string; top: number }) => void;
  onSelect: (id: string) => void;
  address: (t: ContractCommentView, page?: number) => string;
  actionsFor: (t: ContractCommentView) => NoteActions;
  form: (place?: { top: number; pushed: boolean; push: number }) => ReactNode;
}) {
  const byId = useMemo(() => new Map(threads.map((t) => [t.id, t])), [threads]);
  /*
   * Ветка, чью цитату новая версия потеряла, выделения на страницах не имеет:
   * искать её там нечего. Поэтому в подсветку такие ветки не идут, а
   * собираются отдельной группой в конце поля своего документа — иначе
   * разговор пропал бы с экрана вместе с цитатой.
   */
  const anchored = useMemo(() => threads.filter((t) => t.anchorState !== 'MOVED'), [threads]);
  const moved = useMemo(() => threads.filter((t) => t.anchorState === 'MOVED'), [threads]);
  const highlights: DocHighlight[] = useMemo(
    () =>
      anchored.map((t) => ({
        id: t.id,
        quote: t.quote,
        state: t.resolution === 'ACCEPTED' ? 'done' : '',
      })),
    [anchored],
  );

  return (
    <>
      {/* Название места: лента идёт документами друг под другом, и без него
          непонятно, где кончился договор и началась смета. У договора с
          единственным местом заголовок не нужен — называть нечего, — но
          строка остаётся, когда с неё скачивается docx с примечаниями. */}
      {(!single || !!exportUrl) && (
        <div className="doc-place">
          {!single && <div className="section-title">{item.title}</div>}
          <div className="grow" />
          {!!exportUrl && (
            <a
              className="ibtn sm"
              href={exportUrl}
              title={single ? 'Скачать docx с примечаниями' : `Скачать «${item.title}» с примечаниями`}
              target="_blank"
              rel="noreferrer"
            >
              <IconDownload />
            </a>
          )}
        </div>
      )}

      <DocPages
        pdfUrl={pdfUrl}
        scroll={scroll}
        highlights={highlights}
        selectedId={selected}
        onSelectHighlight={onSelectHighlight}
        onSelectText={onSelectText}
        canComment={canComment}
        zoom={zoom}
        markers={mobile}
        clause={(quote) => (snap?.state === 'ready' ? clauseHint(snap.paragraphs, quote) : '')}
      >
        {mobile
          ? undefined
          : (anchors: DocAnchor[]) => {
              const items: MarginItem[] = [];
              for (const a of anchors) {
                const t = byId.get(a.id);
                if (!t) continue;
                items.push({
                  key: t.id,
                  anchorTop: a.top,
                  render: (place) => (
                    <Note
                      thread={t}
                      address={address(t, a.page)}
                      actions={actionsFor(t)}
                      selected={t.id === selected}
                      top={place.top}
                      pushed={place.pushed}
                      push={place.push}
                      onSelect={() => onSelect(t.id)}
                    />
                  ),
                });
              }
              if (draft) {
                items.push({ key: 'draft', anchorTop: draft.top, render: (place) => form(place) });
              }
              items.sort((x, y) => x.anchorTop - y.anchorTop);
              // Сдвинутые ветки — группой в конце поля: якоря у них нет,
              // поэтому `anchorTop` нулевой, а встают они следом за последним
              // пузырём. Выноску им не ломаем — тянуть её не к чему.
              if (moved.length) {
                items.push({
                  key: 'moved-head',
                  anchorTop: 0,
                  render: (place) => (
                    <div className="doc-note" style={{ top: place.top }}>
                      <b>Место изменено</b>
                      <div className="doc-text">
                        Текст, к которому относилась ветка, изменился в новой версии
                      </div>
                    </div>
                  ),
                });
                for (const t of moved) {
                  items.push({
                    key: t.id,
                    anchorTop: 0,
                    render: (place) => (
                      <Note
                        thread={t}
                        address={address(t)}
                        actions={actionsFor(t)}
                        selected={t.id === selected}
                        top={place.top}
                        pushed={false}
                        push={0}
                        onSelect={() => onSelect(t.id)}
                      />
                    ),
                  });
                }
              }
              return <Margin items={items} />;
            }}
      </DocPages>

      {/* Телефон: полей нет, и сдвинутой ветке негде встать у листа — список
          идёт под последней страницей своего документа. Значок «Место
          изменено» ставит сам экран ветки, здесь довольно адреса и первых
          слов. */}
      {mobile && !!moved.length && (
        <>
          <div className="section-title">Место изменено</div>
          <span className="field-hint">
            Текст, к которому относились эти ветки, изменился в новой версии
          </span>
          {moved.map((t) => (
            <button className="row" type="button" key={t.id} onClick={() => onSelect(t.id)}>
              <span className="row-text">
                <span className="row-title">{address(t) || t.quote}</span>
                <span className="row-sub">{t.text}</span>
              </span>
            </button>
          ))}
        </>
      )}
    </>
  );
}

/**
 * Поле пузырей — одно на весь договор. Место якоря приходит в пикселях от
 * верха полотна: их считает `DocPages`, который один знает, где какая
 * страница стоит. Меряются здесь только высоты пузырей — насколько пузырь
 * наезжает на соседа, знает лишь браузер, разложивший его текст.
 */
function Margin({ items }: { items: MarginItem[] }) {
  const box = useRef<HTMLDivElement | null>(null);
  const [sizes, setSizes] = useState<number[]>([]);
  const keys = items.map((it) => it.key).join('|');

  // Меряется своё поддерево, а не документ: уходящий слой стопки рисует ту же
  // разметку второй раз, и общий поиск нашёл бы его свёрнутую копию. Наблюдатель
  // ставится и на само поле (полотно дорисовалось — высота выросла), и на каждый
  // пузырь (раскрылась форма ответа — пузырь стал выше).
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const notes = () => Array.from(el.querySelectorAll<HTMLElement>(':scope > .doc-note'));
    const measure = () => {
      const next = notes().map((n) => n.offsetHeight);
      setSizes((cur) => (cur.length === next.length && cur.every((h, i) => h === next[i]) ? cur : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    notes().forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [keys]);

  const placed = placeNotes(
    items.map((it, i) => ({ item: it, anchorTop: it.anchorTop, height: sizes[i] ?? 0 })),
  );

  return (
    <div className="doc-margin" ref={box}>
      {placed.map((p) => (
        <Fragment key={p.item.key}>
          {p.item.render({ top: p.top, pushed: p.pushed, push: p.top - p.item.anchorTop })}
        </Fragment>
      ))}
    </div>
  );
}

/** Новое замечание: текст, видимость и предложенная формулировка. */
function DraftForm({
  quote,
  place,
  busy,
  ready,
  hint,
  shareable,
  error,
  onCancel,
  onSend,
}: {
  quote: string;
  place?: { top: number; pushed: boolean; push: number };
  busy: boolean;
  /** Можно ли отправлять: ждём только чтения разбора версии. */
  ready: boolean;
  /** Что сказать про пункт: читается разбор или его не удалось получить. */
  hint: string;
  /** Есть ли выбор круга видимости: у гостя замечание всегда общее. */
  shareable: boolean;
  error: string;
  onCancel: () => void;
  onSend: (text: string, proposal: string, shared: boolean) => void;
}) {
  const [text, setText] = useState('');
  const [proposal, setProposal] = useState('');
  const [shared, setShared] = useState(false);

  const body = (
    <>
      <div className="doc-q">«{quote}»</div>
      <textarea
        className="textarea"
        value={text}
        autoFocus
        placeholder="Что не так и что нужно поправить"
        onChange={(e) => setText(e.target.value)}
      />
      {shareable && (
        <label className="check">
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          видит подрядчик
        </label>
      )}
      <input
        className="input"
        value={proposal}
        placeholder="Предложить формулировку"
        onChange={(e) => setProposal(e.target.value)}
      />
      {!!error && (
        <div className="banner crit">
          <b>{error}</b>
        </div>
      )}
      {!!hint && <span className="field-hint">{hint}</span>}
      <div className="doc-foot">
        <span className="grow" />
        <button className="btn link sm quiet" type="button" onClick={onCancel}>
          Отмена
        </button>
        <button
          className="btn link sm"
          type="button"
          disabled={busy || !ready || !text.trim()}
          onClick={() => onSend(text.trim(), proposal.trim(), shared)}
        >
          Отправить
        </button>
      </div>
    </>
  );

  if (!place) return body;
  const style = { top: place.top, '--doc-push': `${Math.round(place.push)}px` } as CSSProperties;
  return (
    <div className={place.pushed ? 'doc-note on pushed' : 'doc-note on'} style={style}>
      {body}
    </div>
  );
}
